// Robert.js
import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

/**
 * -------------------------------------------------------------------
 *  Global fonts  (match AuroraAlea.js + AnybodyBlack)
 * -------------------------------------------------------------------
 */
const GlobalStyles = createGlobalStyle`
  @font-face {
    font-family: 'AnybodyMedium';
    src: url('../assets/fonts/Anybody-Medium.ttf') format('truetype');
    font-style: normal;
    font-weight: 500;
    font-display: swap;
  }
  @font-face {
    font-family: 'AnybodyBlack';
    src: url('../assets/fonts/Anybody-Black.ttf') format('truetype');
    font-style: normal;
    font-weight: 900;
    font-display: swap;
  }
  @font-face {
    font-family: 'LexendMega';
    src: url('../assets/fonts/LexendMega-Regular.ttf') format('truetype');
    font-style: normal;
    font-weight: 400;
    font-display: swap;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    font-family: 'AnybodyMedium', 'Anybody', sans-serif;
    background: #fff;
    color: #000;
  }
`;

/**
 * -------------------------------------------------------------------
 *  Breakpoints
 * -------------------------------------------------------------------
 */
const breakpoints = {
  mobile: '40rem',  // 640px
  tablet: '64rem',  // 1024px
};

/**
 * -------------------------------------------------------------------
 *  Base layout
 * -------------------------------------------------------------------
 */
const Page = styled.article`
  max-width: 115rem;            /* 1440px */
  margin: 0 auto;
  padding: 0 1rem;
  overflow-x: hidden;
`;

const Burger = styled.button`
  position: absolute;
  top: 2.125rem;               /* 34px */
  left: 1.5rem;                /* 24px */
  width: 3rem;                 /* 48px */
  height: 2.5rem;              /* 40px */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 100;

  span {
    display: block;
    width: 100%;
    height: 0.25rem;          /* 4px */
    background: #000;
  }
`;
const HeroImg = styled.img`
  width: 100%;
  height: auto;
  display: block;

  & + & {
    /* мобильная: чуть поднимаем и добавляем отступ снизу */
    @media (max-width: ${breakpoints.mobile}) {
      margin-top: -5rem;
      margin-bottom: 7rem;
    }

    /* планшеты и выше: сильный overlap, без отступа снизу */
    @media (min-width: ${breakpoints.mobile}) {
      margin-top: -15rem; /* ваш сильный overlap */
      margin-bottom: 0;
    }
  }
`;

const Byline = styled.p`
  margin: 1.5rem auto 0;
  max-width: 18rem;
  text-align: center;
  font-family: 'LexendMega', sans-serif;
  font-size: 0.9375rem;        /* 15px */
  font-weight: 400;
  color: #fff;
  background: #000;
  padding: 0.25rem 0.5rem;
  border-radius: 0.15rem;
`;

/**
 * -------------------------------------------------------------------
 *  Typography
 * -------------------------------------------------------------------
 */
const Strapline = styled.h2`
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
  font-style: italic;
  font-weight: 300;
  font-size: clamp(1.75rem, 2.8vw, 2.375rem);    /* 28–38px */
  line-height: 1.35;
  max-width: 76rem;
  margin: -10rem auto 2rem;
  text-align: center;
`;

const Para = styled.p`
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  line-height: 1.55;
  max-width: 66.5rem;
  margin: 1rem auto;
  text-align: center;

  &.italic {
    font-style: italic;
  }

  &.bold {
    font-weight: 900;
  }
`;

const Question = styled(Para)`
  font-style: italic;
  font-weight: 400;
  text-align: left;
`;

const Answer = styled(Para)`
  text-align: right;
`;

const Quote = styled.blockquote`
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  font-weight: 400;
  max-width: 66.5rem;
  margin: 2rem auto;
  text-align: center;
  quotes: "\201C" "\201D" "\2018" "\2019";

  &::before {
    content: open-quote;
  }

  &::after {
    content: close-quote;
  }
`;

/**
 * -------------------------------------------------------------------
 *  Image helpers
 * -------------------------------------------------------------------
 */
const ImgGridTwo = styled.section`
  display: grid;
  gap: ${({ $tight }) => ($tight ? '0.5rem' : '1.5rem')};
  margin: 2rem auto;
  justify-items: center;
  align-items: start;

  /* мобильная версия — одна колонка */
  grid-template-columns: 1fr;

  /* от 40rem (640px) — двухколоночный или кастомный кол-аут */
  @media (min-width: ${breakpoints.mobile}) {
    grid-template-columns: ${({ $cols }) => $cols || 'repeat(2, 1fr)'};
  }
`;

const ImgPairFour = styled(ImgGridTwo)`
  /* мобильная: одна колонка */
  grid-template-columns: 1fr;
  /* убираем левый отступ для мобайла */
  margin-left: 0;
  /* кастомный gap */
  gap: 1rem;

  @media (min-width: ${breakpoints.mobile}) {
    /* на планшетах/десктопе — ваши колонки и отступ */
    grid-template-columns: 0.45fr 1fr;
    margin-left: 10rem;

    /* первая картинка (robert9) */
    & > img:nth-child(1) {
      width: 120%;
      margin-bottom: 2rem;
    }

    /* вторая картинка (robert10) — чуть уже на десктопе */
    & > img:nth-child(2) {
      width: 72%;
      margin-bottom: 0;
    }
  }
`;

const ImgGridThree = styled.section`
  display: grid;
  gap: 1.5rem;
  margin: 2rem auto;
  justify-items: center;
  align-items: center;

  @media (min-width: ${breakpoints.mobile}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StyledImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 0.5rem;
  margin: 0 auto;
`;

/**
 * -------------------------------------------------------------------
 *  Spotify iframe
 * -------------------------------------------------------------------
 */
const SpotifyFrame = styled.iframe`
  display: block;
  width: 90%;
  height: 35rem;
  border: none;
  border-radius: 0.75rem;
  margin: 3rem auto;
`;

/**
 * -------------------------------------------------------------------
 *  Footer (full-width, AnybodyBlack font)
 * -------------------------------------------------------------------
 */
const FooterBar = styled.footer`
  background: #000;
  padding: clamp(3rem, 1vh, 3rem) 0;
  margin: 0 -1rem;            /* ← компенсируем padding у Page */
  text-align: center;

  h1 {
    font-family: 'AnybodyBlack', 'Anybody', sans-serif;
    color: #fff;
    font-size: clamp(2.6rem, 8vw, 8rem);
    font-weight: 900;
    margin: 0;
    line-height: 1;
  }
`;

/**
 * -------------------------------------------------------------------
 *  Component
 * -------------------------------------------------------------------
 */
export default function Robert() {
  return (
    <Page>
      <GlobalStyles />

      {/* Hero section */}
      {/* Hero section */}
      <HeroImg src="/assets/robert1.png" alt="Robert Mercier hero" />
      <HeroImg src="/assets/robert2.png" alt="Robert Mercier hero 2" />

      {/* Strapline */}
      <br/>
      <Strapline>
      ROBERT MERCIER’S LEATHER DESIGNS HAVE GRACED MANY AN A-LISTER, BUT HOW DOES THIS DESIGNER TRULY WORK? GABRIEL MEALOR-PRITCHARD GETS THE INSIDE SCOOP OF HOW THIS CREATIVE DESIGNS FOR THE BEST.
      </Strapline>
      <br/>
      {/* Opening copy */}
      <Para>
      French artisan Robert Mercier has a star-studded clientele and an extensive body of work garnered through collaborations with brands like Maison Margiela, Mugler, and Schiaparelli. With a speciality for unique leather pieces that expand and explore the human body, Mercier has gained notoriety in and out of the fashion world, with one of his most renowned pieces, including the feather micro-bandeau for Ludovic de Saint-Sernin's debut at Ann Demeulemeester. At school, Mercier was given three options in terms of career path: mechanical, numerical or leather work. "The choice was quickly made," Mercier recounted to the Fédération de la Haute Couture et de la Mode (FHCM) last year. After leaving school, the craftsman trained under the Republican Guard, a regiment of France's Gendarmerie, in the saddle department before going on to work for fashion houses including: Louis Vuitton, Hermès, and now working closely alongside Olivier Rousteing at Balmain, describing that he "started out making bags, useful objects, and I've kept this way of looking at things. I make my pieces thinking that their purpose is to store a body inside," he told us. Mercier also quoted to the FHCM, “I really have a dialogue with the material,” so we asked if that “dialogue” had ever led him to an unexpected result. "I'd say the most unexpected result was for the Olivier Rousteing x Jean Paul Gaultier project. I had to make a pair of red leather panties,” he then delved into how those garments were constructed. "As the fittings were scheduled for the following day, I dried them in my garden. The leather was tanned and stretched in the sun, so I pushed it back and rubbed it with a tool, producing a trace of heat stain. Knowing it was the only fitting piece, I sent it oﬀ like that, and the team loved it.” A testament to his dedication and attention to detail when it comes to his craft.
      </Para>
      <br/>
            {/* Image pair 1 */}
            <ImgGridTwo>
        <StyledImg src="/assets/robert3.png" alt="Leather piece close‑up" />
        <StyledImg src="/assets/robert4.png" alt="Mercier in studio" />
      </ImgGridTwo>
      <br/>
      <br/>
      <Para>
      "I started out making bags, useful objects, and I've kept this way of looking at things. I make
      my pieces thinking that their purpose is to store a body inside." – Robert Mercier
      </Para>
      <br/>
      <br/>

      {/* Quote */}
      <Para>

      
Under the supervision of Casey Cadwallader at Mugler, Mercier was commissioned to produce the framework for the iconic bee costume Beyoncé wore during her global Renaissance Tour. One of the many stars who've had the privilege of donning one of this creative's designs. "The project started with an Instagram message. Casey was a follower of my Instagram page, and when I saw that, I sent him a message saying, ‘When are we working together?’ And he replied, ‘Now, if you want’." And just like that, Mercier was granted the opportunity to create a garment that would stun millions across the map. "I think I'll always remember his amazed look when I brought the piece to him," he tells me.
      </Para>
      <Para>
      Though the opportunity was bittersweet. "This moment was very special and remains very mixed for me because at the same time as I was working on this magnificent project, I lost my mother," he shares. In today's fashion landscape, where so much feels so mass-produced, this Frenchman preserves a sense of intimacy and humanity in materials as powerful as leather and porcelain, and through processes so crucial to the crafts achieved by hand. "It's important to protect craftsmanship by preserving traditional techniques, but also to bring in a few innovations to keep it alive," says Mercier. "I always use leathers that are as respectful as possible of the environment and therefore of the men and women who work it," he adds, touching on the ethics of the use of leather within the fashion industry. And it is true, there is a definitive importance in how we construct pieces, to cherish and appreciate the essence of this artisanal skill, which makes it quintessentially bespoke and unique; all while maintaining an ethical stance on where he sources his materials from and the care taken with them once received, with little going to waste.

      </Para>

      {/* Image pair 2 */}
      <ImgGridTwo>
        <StyledImg src="/assets/robert5.png" alt="Robert Mercier piece" />
        <StyledImg src="/assets/robert6.png" alt="Runway garment" />
      </ImgGridTwo>
<br />
      {/* Music paragraph */}
      <Para>
      On the topic of the custom designs, Mercier described his consultation process and how he gets into the mindset to craft a piece. "To develop and create with serenity, my work process starts with a meeting with the customer (an individual or a luxury house) to hear the story behind the project. Then I isolate myself in my studio to let myself interact with the material, to become one with it," embodying the material he will work so closely with for a while. Funnily, due to the artistic nature of Mercier's designs, his pieces look as if captured in motion, frozen. An example of this is the shining armour for the Paris 2024 Olympic Games, which gave the impression that the material was flowing in the wind as the knight rode on horseback atop of the Seine’s shimmering water. 'What attracts you most to capturing this suspended energy?' I ask. "What inspires me most is sculpture. I love the effect of a protective second skin. I love the idea of an object frozen in time that contrasts beautifully with a body in motion," he responds. "I'm touched by this trust," referring to the trust bestowed upon him by some of the largest names in fashion. "I see it as a reward for the rigour and passion I put into my work. It pushes me to go the extra mile to satisfy them," he elaborates. Not only trusted by one or two but by a plethora of notable labels for his expertise, Mercier has a calling from the masses, dealt with modestly and with customary humility. 
      </Para>
      <br />
      <br />
      <br />
      <Question>
      If you composed music the way you design clothes, to which genre would your music belong?
      </Question>
      <br />
      <br />
      <Answer>
      "I would belong to Free Jazz: mastery, precision, freedom, acceptance of the unexpected" – Robert Mercier
      </Answer>

      {/* Image pair 3 */}
      <ImgGridTwo>
        <StyledImg src="/assets/robert7.png" alt="Working on leather" />
        <StyledImg src="/assets/robert8.png" alt="Fitting session" />
      </ImgGridTwo>
      <br />
      <br />

      {/* Consultation process */}
      <Para>
      Every creative has a certain stimulus that sets their imagination in motion, and for Mercier, that spark comes from a steady hum of background music. "I've always worked with background music," he says, "and turning on my speaker is the first thing I do when I enter my studio." It's more than just a habit, it's a ritual. The familiar rhythm of sound acts as a kind of gateway, marking the shift from the outside world to the private space of creativity. "My choice of music is different depending on what I have to do," the craftsman expands. "If I've got an urgent job or I'm running late, I often put on the Red Hot Chilli Peppers’ Californication or Muse’s Black Holes and Revelations. For precision, it's more Massive Attack and Björk, organic music." It is clear, these choices aren't random; they reflect mood and process. Whether it's fuelling momentum with an adrenaline rush of sound, or focusing and refining the pieces with the help of Massive Attack and their carefully constructed tracks. The objects Mericer puts his hands on reflect a certain noise, seen through the process of the garment in question. Music, in this sense, isn't just background music; it's a tool, a tempo-setter and something that mirrors the designer's internal state.
      Just as painters turn to a canvas, Mercier turns to the body. Fascinated by its beauty and versatility, he mentions that it "fascinates me, it's a real playground," it's a living and breathing landscape, curvaceous and irregular. There are no limits. With each client comes a different body, unique and differing from the last – a new challenge and a task Mercier always finds himself up for. "You never finish exploring it and it's much nicer for me to have organic shapes under my fingers all day than squares and rectangles!" he jokes. There's a definitive tactile joy in his work, a sense that creation begins not in geometry, but in the human pulse beneath the moulds he works so closely with. Finally, it had to be asked! What has been his favourite piece to make? "The Dune dress for Zendaya," he states without hesitation. A custom-made, wet-look gown crafted in collaboration with Balmain for the Dune: Part One premiere at the Venice Film Festival in 2021. "This dress almost made itself," he added. There was a quiet reverence in the way he said it, as if the piece possessed magic abilities. "I really felt carried by this project," he adds, highlighting that the flow of this garment's creation was fluid. You get the sense that the dress became a kind of vessel not just for his craftsmanship but for storytelling, the plot of Dune, the sand of Arrakis, and the waves carved by the almighty sand worms, echoing Frank Herbert’s universe of the sci-fi classic. "It all happened naturally, without having to change anything. And it looked amazing on Zendaya," reminisces Mercier.


      In an industry often driven by fast-paced spectacles and over-production, Robert Mercier slows down the beat. Rooted in his skills of patience, purpose, and unwavering respect for his craft. Whether you knock on his Paris-studio door and find him shaping leather into body-hugging armour or translating movement into stillness, Mercier's work speaks with its own melody. His pieces do more than merely dress, they transform, and as he continues to blend traditional techniques of leather work with innovation, one thing is for sure: the song of this craftsman may be sung quietly by the mainstream, but for those who know where to listen, it's a tune you won't forget.
      </Para>
      <br />
      <br />
      {/* Image pair 4 */}

      <ImgPairFour>
        <StyledImg src="/assets/robert9.png" alt="Zendaya Dune dress" />
        <StyledImg src="/assets/robert10.png" alt="Paris 2024 armour" />
      </ImgPairFour>
<br />
<br />

      {/* Playlist intro */}
      <Para className="bold" style={{ fontSize: 'clamp(1.25rem, 2vw, 1.5625rem)' }}>
      ROBERT MERCIER SELECTS HIS TOP THREE FAVOURITE TRACKS THAT FUEL THE BACKGROUND OF THE TIME SPENT CRAFTING HIS MASTERPIECES.
      GIVE THEM A LISTEN BELOW:
      </Para>

      {/* Spotify */}
      <SpotifyFrame
        src="https://open.spotify.com/embed/playlist/2Dsp04kKSKcsy1DoVJdQna?utm_source=generator"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        title="Robert Mercier playlist"
      />

      {/* Footer */}
      <FooterBar>
        <h1>RIFF MAGAZINE</h1>
      </FooterBar>
    </Page>
  );
}
